// =====================================================================
// AuthContext, single source of truth for the Supabase session,
// user profile, and derived role. Wraps the entire router so every
// page can read auth state via useAuth().
//
// Critical perf note:
// ---------------------------------------------------------------------
//   • The Supabase SDK is dynamic-imported (see lib/supabase.js) so
//     the 207 KB vendor-supabase chunk never blocks the landing-page
//     paint.
//   • `loading` starts as `true` but only ProtectedRoute / AdminRoute
//     wait on it, public routes (landing, paper, legal) render
//     immediately even before auth has resolved, because the App
//     router does not gate on `loading` anymore.
//   • Returning visitors get instant render via the localStorage
//     profile cache.
// =====================================================================

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getSupabase } from "../lib/supabase";

const ROLE_ADMIN = "admin";
const ROLE_USER = "user";
const ADMIN_EMAIL_FALLBACK = import.meta.env.VITE_ADMIN_EMAIL || "";

const AuthContext = createContext(null);

// localStorage cache for the profile row. Lets returning visitors
// render the dashboard instantly with last-known role/plan; the live
// row is refreshed in the background and updates the UI if anything
// changed. Worst case the cached version is stale for <1s.
const PROFILE_CACHE_PREFIX = "lm-profile:";

function readCachedProfile(userId) {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(PROFILE_CACHE_PREFIX + userId);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeCachedProfile(userId, profile) {
  if (!userId || !profile) return;
  try {
    localStorage.setItem(PROFILE_CACHE_PREFIX + userId, JSON.stringify(profile));
  } catch {
    /* quota / disabled storage, ignore */
  }
}

function clearCachedProfile(userId) {
  if (!userId) return;
  try {
    localStorage.removeItem(PROFILE_CACHE_PREFIX + userId);
  } catch {}
}

function syntheticProfile(user) {
  if (!user) return null;
  const isAdmin =
    ADMIN_EMAIL_FALLBACK &&
    user.email?.toLowerCase() === ADMIN_EMAIL_FALLBACK.toLowerCase();
  return {
    id: user.id,
    email: user.email,
    role: isAdmin ? ROLE_ADMIN : ROLE_USER,
    created_at: user.created_at ?? new Date().toISOString(),
    full_name: user.user_metadata?.full_name ?? null,
    polar_customer_id: null,
    plan: null,
  };
}

/**
 * Fetch the row from the `profiles` table that matches this auth user.
 * Falls back to a synthetic profile if no row exists yet (e.g. new
 * signup before the DB trigger fires), or if the query takes longer
 * than 5 s, so the boot screen never deadlocks on a slow Supabase
 * round-trip.
 */
async function fetchProfile(supabase, user) {
  if (!supabase || !user) return null;

  const query = supabase
    .from("profiles")
    .select(
      "id, email, role, created_at, full_name, polar_customer_id, plan, pricing_override, proxy_url, proxy_gate_key"
    )
    .eq("id", user.id)
    .maybeSingle();

  const timeout = new Promise((resolve) =>
    setTimeout(
      () => resolve({ data: null, error: { code: "TIMEOUT", message: "profile fetch timed out" } }),
      5000
    )
  );

  let data = null;
  let error = null;
  try {
    ({ data, error } = await Promise.race([query, timeout]));
  } catch (e) {
    // Supabase-js doesn't normally throw, but if it does we treat it
    // the same as a profile-not-found and use the synthetic fallback.
    // eslint-disable-next-line no-console
    console.warn("[auth] profile fetch threw:", e?.message ?? e);
  }

  if (error && error.code !== "PGRST116") {
    // eslint-disable-next-line no-console
    console.warn("[auth] failed to load profile:", error.message ?? error);
  }

  return data ?? syntheticProfile(user);
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  // profileVerified: tracks whether the current profile object came
  // from a TRUSTED source (a real Supabase fetch, or a localStorage
  // cache populated from a previous successful fetch) vs an UNTRUSTED
  // placeholder (the synthetic fallback used while step D is in
  // flight on a cache-cold visit).
  //
  // AdminRoute reads this to avoid a known race: on a first-time
  // admin visit with no cached profile, the synthetic placeholder
  // defaults role to "user", which made AdminRoute redirect to
  // /portal before the network fetch returned the real admin role.
  // The user saw the admin page fail to load until they refreshed
  // (which then hit the now-warm cache and rendered correctly).
  //
  // Anonymous (no session) sets verified to true too, since there's
  // nothing left to wait for.
  const [profileVerified, setProfileVerified] = useState(false);
  // Tracks the user.id we already fetched a profile for. Dedupes
  // fetches across the boot effect + every onAuthStateChange event
  // (INITIAL_SESSION, SIGNED_IN, TOKEN_REFRESHED) so we don't hit
  // Supabase 3-5 times in a row on every page load.
  const lastFetchedUserId = useRef(null);
  // Keeps the unsubscribe handle from onAuthStateChange so we can
  // tear it down on unmount even though setup is async.
  const subscriptionRef = useRef(null);

  // Boot + auth-state subscription, all guarded by a single dynamic
  // import of the Supabase SDK. While the chunk is downloading the
  // landing page is already painted (App.jsx no longer gates on
  // `loading` for public routes).
  useEffect(() => {
    let cancelled = false;

    (async () => {
      const supabase = await getSupabase();
      if (cancelled) return;

      // Step A: read the persisted session (fast, ~10 ms from localStorage
      // once the SDK is in memory).
      let s = null;
      try {
        const result = await supabase.auth.getSession();
        s = result?.data?.session ?? null;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[auth] getSession threw:", e?.message ?? e);
      }
      if (cancelled) return;
      setSession(s);

      // Step B: hydrate profile from cache (instant) or synthetic fallback.
      //    This lets the app render immediately while the network refresh
      //    runs in the background. If the cached row is stale (role
      //    changed server-side, etc.) step D will overwrite it.
      const userId = s?.user?.id ?? null;
      let cachedProfile = null;
      if (userId) {
        cachedProfile = readCachedProfile(userId);
        setProfile(cachedProfile ?? syntheticProfile(s.user));
        // If we have a cache hit, treat the role as verified: the
        // cache was written from a previous successful network fetch,
        // so AdminRoute can trust it for routing decisions. If we
        // only have the synthetic fallback we leave verified=false
        // and step D below will flip it once the real row arrives.
        if (cachedProfile) {
          setProfileVerified(true);
        }
      } else {
        setProfile(null);
        // No session, nothing to wait for, mark verified so
        // ProtectedRoute / AdminRoute can redirect immediately.
        setProfileVerified(true);
      }

      // Step C: release any auth-gated UI (ProtectedRoute / AdminRoute).
      // Everything below runs without blocking the UI.
      setLoading(false);

      // Step D: refresh the profile from Supabase. Background-only
      // the user sees the cached version until this resolves, then the
      // UI re-renders if the row changed.
      if (userId) {
        lastFetchedUserId.current = userId;
        try {
          const fresh = await fetchProfile(supabase, s.user);
          if (!cancelled && fresh) {
            setProfile(fresh);
            writeCachedProfile(userId, fresh);
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn("[auth] background profile refresh threw:", e?.message ?? e);
        } finally {
          // Either the fetch succeeded (profile updated, cache
          // refreshed) or it failed (we keep the synthetic fallback).
          // In both cases the wait is over; AdminRoute can now make
          // its routing decision without risk of a false redirect.
          if (!cancelled) setProfileVerified(true);
        }
      }

      // Step E: subscribe to auth state changes for the rest of the
      // session. `onAuthStateChange` fires on sign-in, sign-out, token
      // refresh, and OAuth redirect completion. We dedupe on user.id so
      // token refreshes don't re-fetch the same profile every hour, and
      // we update the cache whenever a fresh profile arrives.
      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange(async (_event, sNew) => {
        if (cancelled) return;
        setSession(sNew);
        const newUserId = sNew?.user?.id ?? null;
        if (newUserId === lastFetchedUserId.current) return;
        lastFetchedUserId.current = newUserId;
        if (!newUserId) {
          setProfile(null);
          // Signed out: no profile to verify, mark resolved.
          setProfileVerified(true);
          return;
        }
        // New user landed via OAuth or password sign-in. If we have
        // a cached row, trust it for routing while the fresh fetch
        // runs; otherwise drop verified=false until the fetch lands.
        const cached = readCachedProfile(newUserId);
        if (cached) {
          setProfile(cached);
          setProfileVerified(true);
        } else {
          setProfile(syntheticProfile(sNew.user));
          setProfileVerified(false);
        }
        try {
          const fresh = await fetchProfile(supabase, sNew.user);
          if (!cancelled && fresh) {
            setProfile(fresh);
            writeCachedProfile(newUserId, fresh);
          }
        } finally {
          if (!cancelled) setProfileVerified(true);
        }
      });
      subscriptionRef.current = subscription;
    })();

    return () => {
      cancelled = true;
      try {
        subscriptionRef.current?.unsubscribe();
      } catch {}
    };
  }, []);

  // Token-refresh nudge on tab focus / visibility change.
  // -------------------------------------------------------------------
  // Supabase's `autoRefreshToken: true` (configured in lib/supabase.js)
  // uses setInterval to refresh the access token a few minutes before
  // the 1-hour deadline. That timer is unreliable on backgrounded
  // tabs:
  //   - Chrome throttles backgrounded-tab timers to ~1 per minute
  //   - When the laptop sleeps, no JS timers fire at all
  //   - Tab discard / process suspension cancels the interval entirely
  // So if a user leaves the tab open for an hour while doing something
  // else, they come back to a session whose access token has expired
  // and whose internal refresh tick has not yet fired. The first
  // navigation (e.g. clicking "Open admin") makes the admin pages
  // query Supabase with a dead token; queries fail or hang, the page
  // appears empty, and only a hard reload recovers because the boot
  // effect above re-runs getSession() which DOES trigger a refresh.
  //
  // Fix: every time the tab regains focus or visibility, call
  // getSession() ourselves. That same call inside Supabase's auth
  // module reads the persisted session, notices the access token is
  // expired, and triggers an immediate refresh. The onAuthStateChange
  // subscription registered above then fires with TOKEN_REFRESHED and
  // surfaces the new session into React state. Subsequent queries on
  // the freshly-foregrounded tab use the rotated token.
  //
  // We dedupe with an `inFlight` flag so rapid focus/blur churn (e.g.
  // alt-tabbing between two open windows) doesn't queue up overlapping
  // refresh calls.
  useEffect(() => {
    let cancelled = false;
    let inFlight = false;

    const nudge = async () => {
      if (cancelled || inFlight) return;
      if (document.visibilityState !== "visible") return;
      inFlight = true;
      try {
        const supabase = await getSupabase();
        await supabase.auth.getSession();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[auth] focus-refresh threw:", e?.message ?? e);
      } finally {
        inFlight = false;
      }
    };

    document.addEventListener("visibilitychange", nudge);
    window.addEventListener("focus", nudge);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", nudge);
      window.removeEventListener("focus", nudge);
    };
  }, []);

  // Force-refetch the profile from Supabase. Used by /portal/billing
  // after a Polar checkout to poll for the webhook to update plan +
  // polar_customer_id. Returns the fresh profile or null if no
  // session.
  const refreshProfile = async () => {
    const u = session?.user;
    if (!u) return null;
    const supabase = await getSupabase();
    const fresh = await fetchProfile(supabase, u);
    if (fresh) {
      setProfile(fresh);
      writeCachedProfile(u.id, fresh);
    }
    return fresh;
  };

  const value = useMemo(
    () => ({
      session,
      user: session?.user ?? null,
      profile,
      role: profile?.role ?? null,
      isAdmin: profile?.role === ROLE_ADMIN,
      isAuthenticated: !!session?.user,
      loading,
      profileVerified,
      refreshProfile,
      signOut: async () => {
        const userId = lastFetchedUserId.current;
        const supabase = await getSupabase();
        await supabase.auth.signOut();
        setSession(null);
        setProfile(null);
        // No user means nothing to verify. Mark as resolved so
        // ProtectedRoute / AdminRoute can redirect to /login
        // without an extra spinner tick.
        setProfileVerified(true);
        lastFetchedUserId.current = null;
        clearCachedProfile(userId);
      },
    }),
    [session, profile, loading, profileVerified]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

export { ROLE_ADMIN, ROLE_USER };
